import { Card, Text } from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./ActionsDropdown";

interface Props {
  id: string;
  name: string | null;
  checkCount: number;
  caseCount?: number;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  refreshPackage: () => void;
}
export function TableDataCard(props: Props) {
  const {
    id,
    name,
    checkCount,
    caseCount = 0,
    createdDate,
    modifiedDate,
    isActive,
    refreshPackage,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { packageImprovements } = useFlags();

  return (
    <Card>
      <div
        className={styles.container}
        role="presentation"
        onClick={() => {
          const viewOnly = packageImprovements && caseCount > 0;
          navigate(viewOnly ? `/package/${id}?view=true` : `/package/${id}`, {
            state: {
              last: { href: `${location.pathname}${location.search}` },
            },
          });
        }}
        data-testid="sm-package-selectable-container"
      >
        <div className={styles.topHalf}>
          <div className={styles.userDetailsContainer}>
            <div className={styles.userDetails}>
              <Text style="global/heading-medium" testID="sm-package-name">
                {name || "--"}
              </Text>
              {!isActive && (
                <Text testID="sm-package-active" style="global/info-label">
                  {t("(Deactivated)")}
                </Text>
              )}
            </div>
          </div>
          <ActionsDropdown
            id={id}
            isActive={isActive}
            packageName={name || ""}
            caseCount={caseCount}
            refreshPackage={refreshPackage}
          />
        </div>
        <div className={styles.bottomHalf}>
          <div className={css(styles.column, { flexBasis: "60%" })}>
            <Text style="global/label-small" color="text-weak">
              {t("Checks")}
            </Text>
            <Text style="form/body-secondary" testID="sm-package-check-count">
              {packageImprovements
                ? checkCount
                : `${checkCount} ${t("checks")}`}
            </Text>
          </div>
          <div className={styles.column}>
            <Text
              style="global/label-small"
              color="text-weak"
              testID="sm-package-created"
            >
              {packageImprovements ? t("Date Modified") : t("Created date")}
            </Text>
            <Text style="form/body-secondary">
              {formatDate(
                packageImprovements && modifiedDate ? modifiedDate : createdDate
              )}
            </Text>
          </div>
        </div>
      </div>
    </Card>
  );
}

const styles = {
  container: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    borderRadius: "var(--border-radius-3)",
    cursor: "pointer",
  }),
  topHalf: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  userDetailsContainer: css({
    display: "flex",
    alignItems: "center",
    gap: 10,
  }),
  userDetails: css({
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
  }),
  bottomHalf: css({
    display: "flex",
    flexDirection: "row",
    gap: "var(--space-2)",
  }),
  column: css({
    display: "flex",
    flexDirection: "column",
  }),
};
